/**
 * Testimonial Carousel Custom Element
 * Multi-card sliding carousel with auto-play, touch swipe, keyboard nav.
 *
 * Follows the same pattern as HeroSlider in new-hero-slider.js.
 */
class TestimonialCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 0;
    this.totalPages = 0;
    this.autoRotateTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  connectedCallback() {
    this.track = this.querySelector('.testimonials__track');
    this.slides = Array.from(this.querySelectorAll('.testimonials__slide'));
    this.dotsContainer = this.querySelector('.testimonials__dots');
    this.prevBtn = this.querySelector('.testimonials__arrow--prev');
    this.nextBtn = this.querySelector('.testimonials__arrow--next');
    this.statusElement = this.querySelector('.testimonials__status');

    if (this.slides.length === 0) return;

    this.autoRotate = this.dataset.autoRotate === 'true';
    this.rotationSpeed = parseInt(this.dataset.rotationSpeed, 10) || 5;
    this.cardsPerViewSetting = parseInt(this.dataset.cardsPerView, 10) || 3;

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.prefersReducedMotion) {
      this.autoRotate = false; // Disable auto-rotate if reduced motion is preferred
    }

    // Determine text direction for RTL support
    this.isRtl = document.dir === 'rtl' || (this.closest('[dir="rtl"]') !== null);

    this.calculateLayout();
    this.generateDots();
    this.bindEvents();
    this.setupTouchEvents();
    this.setupKeyboardNav();
    
    // Show initial page without transition animation
    this.goToPage(0, false);

    if (this.autoRotate && this.totalPages > 1) {
      this.startAutoRotate();
      this.setupHoverPause();
    }

    // Recalculate layout on window resize (debounced)
    this._resizeHandler = this.debounce(() => {
      this.calculateLayout();
      this.generateDots();
      this.goToPage(Math.min(this.currentPage, this.totalPages - 1), false);
    }, 200);
    window.addEventListener('resize', this._resizeHandler);
  }

  disconnectedCallback() {
    this.stopAutoRotate();
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  calculateLayout() {
    // Determine actual cards per view based on viewport width
    const viewportWidth = window.innerWidth;
    if (viewportWidth < 750) {
      this.cardsPerView = 1;
    } else if (viewportWidth < 990) {
      this.cardsPerView = Math.min(this.cardsPerViewSetting, 2);
    } else {
      this.cardsPerView = this.cardsPerViewSetting;
    }
    
    // Total pages is determined by dividing total slides by cards per view
    this.totalPages = Math.ceil(this.slides.length / this.cardsPerView);
  }

  generateDots() {
    if (!this.dotsContainer) return;
    
    this.dotsContainer.innerHTML = '';
    
    // If there is only 1 page, we don't need dot indicators
    if (this.totalPages <= 1) return;

    for (let i = 0; i < this.totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'testimonials__dot';
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      if (i === this.currentPage) {
        dot.setAttribute('aria-current', 'true');
      }
      
      dot.addEventListener('click', () => {
        this.goToPage(i);
        this.restartAutoRotate();
      });
      
      this.dotsContainer.appendChild(dot);
    }
    
    this.dots = Array.from(this.dotsContainer.querySelectorAll('.testimonials__dot'));
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.prev();
        this.restartAutoRotate();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.next();
        this.restartAutoRotate();
      });
    }
  }

  setupTouchEvents() {
    const viewport = this.querySelector('.testimonials__viewport');
    if (!viewport) return;

    viewport.addEventListener('touchstart', (e) => {
      this.touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    viewport.addEventListener('touchend', (e) => {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    }, { passive: true });
  }

  handleSwipe() {
    const swipeThreshold = 50;
    const deltaX = this.touchEndX - this.touchStartX;

    if (Math.abs(deltaX) > swipeThreshold) {
      const swipedLeft = deltaX < 0;
      
      if (swipedLeft) {
        // Swipe left: In LTR next, in RTL prev
        this.isRtl ? this.prev() : this.next();
      } else {
        // Swipe right: In LTR prev, in RTL next
        this.isRtl ? this.next() : this.prev();
      }
      
      this.restartAutoRotate();
    }
  }

  setupKeyboardNav() {
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.isRtl ? this.next() : this.prev();
        this.restartAutoRotate();
      } else if (e.key === 'ArrowRight') {
        this.isRtl ? this.prev() : this.next();
        this.restartAutoRotate();
      }
    });
  }

  setupHoverPause() {
    if (window.matchMedia('(hover: hover)').matches) {
      this.addEventListener('mouseenter', () => this.stopAutoRotate());
      this.addEventListener('mouseleave', () => this.startAutoRotate());
    }
  }

  goToPage(pageIndex, animate = true) {
    if (this.totalPages <= 0) return;

    // Wrap around
    let newPage = pageIndex;
    if (newPage < 0) {
      newPage = this.totalPages - 1;
    } else if (newPage >= this.totalPages) {
      newPage = 0;
    }

    this.currentPage = newPage;

    // Calculate translateX offset percent
    const slideWidthPercent = 100 / this.cardsPerView;
    const offsetPercent = newPage * this.cardsPerView * slideWidthPercent;
    const direction = this.isRtl ? 1 : -1;

    if (!animate) {
      this.track.style.transition = 'none';
    }
    
    this.track.style.transform = `translateX(${direction * offsetPercent}%)`;
    
    if (!animate) {
      void this.track.offsetWidth; // Force a reflow
      this.track.style.transition = '';
    }

    // Update dot states
    if (this.dots) {
      this.dots.forEach((dot, i) => {
        if (i === newPage) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    // Screen reader announcement
    if (this.statusElement) {
      this.statusElement.textContent = `Showing page ${newPage + 1} of ${this.totalPages}`;
    }
  }

  next() {
    this.goToPage(this.currentPage + 1);
  }

  prev() {
    this.goToPage(this.currentPage - 1);
  }

  startAutoRotate() {
    if (!this.autoRotate || this.prefersReducedMotion || this.totalPages <= 1) return;
    this.stopAutoRotate();
    this.autoRotateTimer = setInterval(() => {
      this.next();
    }, this.rotationSpeed * 1000);
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }
  }

  restartAutoRotate() {
    if (this.autoRotate) {
      this.startAutoRotate();
    }
  }

  debounce(fn, delay) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Register the custom element
if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
